// Display helpers for a job's code metrics (internal/compiler
// plans/2026-10-12-code-metrics.md). Pure functions, unit-tested in
// codeMetrics.test.ts. Work units are internal-only and never displayed.

/** The code-metric columns of a jobs row (internal/api migrations/
 *  0014_jobs_code_metrics.sql). dynamic_code is SQLite 0/1. */
export interface JobCodeMetricsRow {
  total_lines_of_code: number | null;
  compiled_lines_of_code: number | null;
  dynamic_code: number | null;
}

/** Tooltip shared by every place that shows the dynamic-code flag. */
export const DYNAMIC_CODE_TOOLTIP_YES =
  "Dynamic code: yes. The program needs eval, new Function, vm or dynamic require at run time, so the interpreter was included in the binary.";
export const DYNAMIC_CODE_TOOLTIP_NO =
  "Dynamic code: no. Everything was compiled ahead of time; the interpreter was not included in the binary.";
export const LINES_OF_CODE_TOOLTIP =
  "Statements and declarations in every JS/TS file the build loaded (entry and dependencies), before tree shaking. Comments, blank lines and type-only code don't count.";
export const COMPILED_LINES_TOOLTIP =
  "Lines of code left after tree shaking, plus constant eval / new Function code compiled ahead of time.";

/** Thousands-separated count, or an em dash when unknown. */
export function fmtLines(n: number | null | undefined): string {
  return n == null ? "—" : n.toLocaleString("en-US");
}

/** "yes" / "no" / "—" for the dynamic-code flag (SQLite 0/1 or boolean). */
export function dynamicCodeLabel(
  v: number | boolean | null | undefined,
): string {
  if (v == null) return "—";
  return v === true || v === 1 ? "yes" : "no";
}

export function dynamicCodeTooltip(
  v: number | boolean | null | undefined,
): string | undefined {
  if (v == null) return undefined;
  return v === true || v === 1
    ? DYNAMIC_CODE_TOOLTIP_YES
    : DYNAMIC_CODE_TOOLTIP_NO;
}

/** Share of the loaded code that was compiled, e.g. "26%" — empty when
 *  either side is unknown or nothing was loaded. Can exceed 100% when
 *  flattened eval code outweighs what tree shaking removed. */
export function compiledShare(row: JobCodeMetricsRow): string {
  const { total_lines_of_code: t, compiled_lines_of_code: c } = row;
  if (t == null || c == null || t === 0) return "";
  return `${Math.round((c / t) * 100)}%`;
}
